import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2 } from '@/components/icons';
import { useGenerateGetUrl } from '../lib/convex-hooks';
import { useMessage } from '../lib/convex-hooks';
import type { Id } from '@pronteer/convex/dataModel';
import { openWithNativeViewer } from '@/lib/native-file-viewer';
import { ImageGrid, type GridImage } from './ImageGrid';

interface Attachment {
  storageId: Id<'_storage'>;
  type: string;
  name: string;
  // 400px grid thumbnail (images sent after thumbnails were introduced).
  thumbStorageId?: Id<'_storage'>;
}

interface AttachmentPreviewProps {
  messageId: Id<'messages'>;
  attachmentType?: string;
  attachmentName?: string;
  attachments?: Attachment[];
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

function isImageType(type: string) {
  return IMAGE_TYPES.includes(type);
}

/** Resolves URLs for multiple attachments */
function useAttachmentUrls(attachments: Attachment[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const getUrl = useGenerateGetUrl();

  useEffect(() => {
    if (attachments.length === 0) return;

    // Resolve the original (lightbox) and, when present, the thumbnail (tile).
    const imageStorageIds = attachments
      .filter((a) => isImageType(a.type))
      .flatMap((a) => (a.thumbStorageId ? [a.storageId, a.thumbStorageId] : [a.storageId]));
    const unresolved = imageStorageIds.filter((id) => !urls[id]);
    if (unresolved.length === 0) return;

    let isMounted = true;
    setLoading(true);

    Promise.all(
      unresolved.map(async (storageId) => {
        const url = await getUrl({ storageId });
        return { storageId, url };
      })
    )
      .then((results) => {
        if (!isMounted) return;
        setUrls((prev) => {
          const next = { ...prev };
          for (const r of results) {
            if (r.url) next[r.storageId] = r.url;
          }
          return next;
        });
      })
      .catch((e) => console.error('Failed to get URLs:', e))
      .finally(() => { if (isMounted) setLoading(false); });

    return () => { isMounted = false; };
  }, [attachments, urls, getUrl]);

  return { urls, loading };
}

export function AttachmentPreview({ messageId, attachmentType, attachmentName, attachments }: AttachmentPreviewProps) {
  const message = useMessage(messageId);
  const getUrl = useGenerateGetUrl();

  // Normalize: build a unified attachments list from either new array or legacy singular fields
  const normalizedAttachments: Attachment[] = React.useMemo(() => {
    if (attachments && attachments.length > 0) return attachments;
    if (message?.attachmentStorageId) {
      return [{
        storageId: message.attachmentStorageId,
        type: attachmentType || 'application/octet-stream',
        name: attachmentName || 'Attachment',
      }];
    }
    return [];
  }, [attachments, message?.attachmentStorageId, attachmentType, attachmentName]);

  const { urls, loading: urlsLoading } = useAttachmentUrls(normalizedAttachments);
  const [openingId, setOpeningId] = useState<string | null>(null);

  if (normalizedAttachments.length === 0) return null;

  const images = normalizedAttachments.filter((a) => isImageType(a.type));
  const files = normalizedAttachments.filter((a) => !isImageType(a.type));

  const handleDownload = async (storageId: Id<'_storage'>, fileName?: string) => {
    setOpeningId(storageId);
    try {
      const url = await getUrl({ storageId });
      if (!url) return;
      const handled = await openWithNativeViewer(url, fileName);
      if (!handled) window.open(url, '_blank');
    } catch (e) {
      console.error('Failed to get attachment URL:', e);
    } finally {
      setOpeningId(null);
    }
  };

  const gridImages: GridImage[] = images.map((a) => ({
    id: a.storageId,
    url: urls[a.storageId] ?? null,
    thumbUrl: a.thumbStorageId ? (urls[a.thumbStorageId] ?? null) : null,
    name: a.name,
  }));

  return (
    <div className="flex flex-col gap-2 mt-1">
      {images.length > 0 && <ImageGrid images={gridImages} loading={urlsLoading} />}

      {/* File list */}
      {files.map((file) => {
        const isOpening = openingId === file.storageId;
        return (
          <button
            key={file.storageId}
            onClick={() => handleDownload(file.storageId, file.name)}
            disabled={isOpening}
            className="bg-background-brand-bold flex items-center gap-2 px-2 h-8 rounded-[10px] w-full cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-70"
          >
            <FileText className="w-4 h-4 text-white shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-[12px] leading-normal opacity-70 truncate text-left">
                {file.name}
              </p>
            </div>
            {isOpening ? (
              <Loader2 className="w-4 h-4 text-white shrink-0 animate-spin" />
            ) : (
              <Download className="w-4 h-4 text-white shrink-0" />
            )}
          </button>
        );
      })}
    </div>
  );
}

